import {
  type FileQueryFilter,
  type MetadataFilter,
  type PipelineFilter,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import { type SampleExplorerViewState } from "common";

export const serializeMetadataFilters = (
  filters: MetadataFilter[]
): SampleExplorerViewState["metadata"]["filters"] =>
  filters.map(filter => ({
    field: filter.field,
    operator: filter.operator,
    value:
      filter.value instanceof Date ? filter.value.toISOString() : filter.value,
  }));

export const serializeFileQueryFilters = (
  filters: FileQueryFilter[]
): SampleExplorerViewState["reports"]["filters"] =>
  filters.map(filter => ({
    reportRelativePath: filter.reportRelativePath,
    fields: Object.fromEntries(
      Object.entries(filter.fields).map(([fieldName, config]) => [
        fieldName,
        {
          aggregation: config.aggregation,
        },
      ])
    ),
    filter: filter.filter
      ? filter.filter.and
        ? {
            and: filter.filter.and.map(condition =>
              Object.fromEntries(
                Object.entries(condition).map(([fieldName, operators]) => [
                  fieldName,
                  Object.fromEntries(
                    Object.entries(operators).map(([operator, value]) => [
                      operator,
                      value instanceof Date ? value.toISOString() : value,
                    ])
                  ),
                ])
              )
            ),
          }
        : filter.filter.or
          ? {
              or: filter.filter.or.map(condition =>
                Object.fromEntries(
                  Object.entries(condition).map(([fieldName, operators]) => [
                    fieldName,
                    Object.fromEntries(
                      Object.entries(operators).map(([operator, value]) => [
                        operator,
                        value instanceof Date ? value.toISOString() : value,
                      ])
                    ),
                  ])
                )
              ),
            }
          : undefined
      : undefined,
  }));

export const deserializeMetadataFilters = (
  filters: SampleExplorerViewState["metadata"]["filters"]
): MetadataFilter[] =>
  filters.map(filter => ({
    field: filter.field,
    operator: filter.operator,
    value: filter.value,
  }));

export const deserializePipelineFilters = (
  filters: SampleExplorerViewState["pipeline"]["filters"]
): PipelineFilter[] =>
  filters.map(filter => ({
    field: filter.field,
    operator: filter.operator,
    value: filter.value,
  }));

export const deserializeFileQueryFilters = (
  filters: SampleExplorerViewState["reports"]["filters"]
): FileQueryFilter[] =>
  filters.map(filter => ({
    reportRelativePath: filter.reportRelativePath,
    fields: Object.keys(filter.fields).reduce<FileQueryFilter["fields"]>(
      (acc, fieldName) => {
        acc[fieldName] = {
          aggregation: filter.fields[fieldName].aggregation,
        };
        return acc;
      },
      {}
    ),
    filter: filter.filter
      ? filter.filter.and
        ? { and: filter.filter.and }
        : filter.filter.or
          ? { or: filter.filter.or }
          : undefined
      : undefined,
  }));
